import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  format,
  startOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  eachWeekOfInterval,
  eachMonthOfInterval,
  eachQuarterOfInterval,
  getQuarter,
  addMonths,
  addDays,
  differenceInCalendarDays,
  min as dfMin,
  max as dfMax,
} from "date-fns";
import { paginateSupabase } from "@/lib/supabase-paginate";
import {
  convertPurchaseAtBankRate,
  type PnlPurchaseRate,
} from "@/lib/pnl-purchases";
import type {
  CashFlowRow,
  DisplayCurrency,
  ViewPeriod,
} from "@/hooks/finance/cash-flow/useCashFlow";

import { PNL_PURCHASES_LINE } from "@/components/finance/reports/pnl-report/mapping";

const norm = (s: string) => (s || "").trim().toLowerCase();
const PNL_CALCULATION_VERSION = "dedicated-bank-rate-purchases-v3";

/** True when a group/position pair falls inside the P&L "Purchases" line. */
function isPurchaseLine(groupLabel: string, positionLabel: string): boolean {
  return (PNL_PURCHASES_LINE.matches as any[]).some((m) => {
    if (norm(m.group) !== norm(groupLabel)) return false;
    if (Array.isArray(m.positions)) {
      return m.positions.some((p: string) => norm(p) === norm(positionLabel));
    }
    if (Array.isArray(m.excludePositions)) {
      return !m.excludePositions.some((p: string) => norm(p) === norm(positionLabel));
    }
    return true;
  });
}

type ExchangeRateEntry = PnlPurchaseRate;

export function useProfitAndLoss(
  dateFrom: string,
  dateTo: string,
  viewPeriod: ViewPeriod = "monthly",
  displayCurrency: DisplayCurrency = "ETB"
) {
  const { user } = useAuth();

  const exchangeRatesQuery = useQuery({
    queryKey: ["pnl-exchange-rates", PNL_CALCULATION_VERSION, dateFrom, dateTo],
    queryFn: async () => {
      const data = await paginateSupabase<any>((from, to) =>
        supabase
          .from("exchange_rates" as any)
          .select("rate, effective_date, rate_type, currency_pair, bank_account_id")
          .in("currency_pair", [
            "USD/ETB", "EUR/ETB", "GBP/ETB",
            "USD/EUR", "EUR/USD", "USD/GBP",
            "GBP/USD", "EUR/GBP", "GBP/EUR",
          ])
          .lte("effective_date", dateTo)
          .order("effective_date", { ascending: false })
          .range(from, to) as any
      );
      return (data || []) as unknown as ExchangeRateEntry[];
    },
    enabled: !!user && !!dateFrom && !!dateTo,
    refetchOnMount: "always",
  });

  // Accrual: pull ALL expenses by expense_date, regardless of payment_status.
  // Widen the window so multi-month amortized expenses (rent) that began
  // before the report period can still contribute to periods inside it.
  // 60 months covers straight-line depreciation of fixed assets (5 years).
  const amortWindowFrom = useMemo(() => {
    if (!dateFrom) return dateFrom;
    return format(addMonths(new Date(dateFrom), -60), "yyyy-MM-dd");
  }, [dateFrom]);

  // Fixed asset category — capitalized purchases that depreciate over 60 months
  // instead of hitting the P&L in the month of purchase.
  const ASSET_CATEGORY_ID = "de427b28-764e-4334-b4c4-79273cfe31c1";
  const DEPRECIATION_MONTHS = 60;

  const expensesQuery = useQuery({
    queryKey: ["pnl-expenses", PNL_CALCULATION_VERSION, amortWindowFrom, dateTo],
    queryFn: async () => {
      return await paginateSupabase<any>((from, to) =>
        (supabase as any)
          .from("expenses")
          .select(
            "id, expense_date, amount, total_amount, net_payment, currency, coverage_months, category_id, bank_account_id, payment_status"
          )
          .gte("expense_date", amortWindowFrom)
          .lte("expense_date", dateTo)
          .neq("payment_status", "rejected")
          .range(from, to)
      );
    },
    enabled: !!user && !!amortWindowFrom && !!dateTo,
    refetchOnMount: "always",
  });

  const categoriesQuery = useQuery({
    queryKey: ["pnl-categories"],
    queryFn: async () => {
      return await paginateSupabase<any>((from, to) =>
        supabase
          .from("expense_categories")
          .select("id, name, cash_flow_type, category_type, parent_id")
          .range(from, to)
      );
    },
    enabled: !!user,
  });

  const salesQuery = useQuery({
    queryKey: ["pnl-chipchip-gmv", dateFrom, dateTo],
    queryFn: async () => {
      const rows = await paginateSupabase<{ date: string; total_gmv: number }>((from, to) =>
        (supabase as any)
          .from("chipchip_daily_accepted_totals")
          .select("date, total_gmv")
          .gte("date", dateFrom)
          .lte("date", dateTo)
          .range(from, to)
      );
      return rows;
    },
    enabled: !!user && !!dateFrom && !!dateTo,
  });

  const feesQuery = useQuery({
    queryKey: ["pnl-chipchip-fees", dateFrom, dateTo],
    queryFn: async () => {
      return await paginateSupabase<{ date: string; combined_total: number }>((from, to) =>
        (supabase as any)
          .from("chipchip_daily_revenue_fees")
          .select("date, packaging_fee_total, delivery_charge_total, combined_total")
          .gte("date", dateFrom)
          .lte("date", dateTo)
          .range(from, to)
      );
    },
    enabled: !!user && !!dateFrom && !!dateTo,
  });

  const b2bQuery = useQuery({
    queryKey: ["pnl-b2b", dateFrom, dateTo],
    queryFn: async () => {
      return await paginateSupabase<any>((from, to) =>
        supabase
          .from("b2b_sales_data")
          .select("sale_date, total_revenue")
          .gte("sale_date", dateFrom)
          .lte("sale_date", dateTo)
          .range(from, to)
      );
    },
    enabled: !!user && !!dateFrom && !!dateTo,
  });

  const bankAccountsQuery = useQuery({
    queryKey: ["pnl-bank-accounts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bank_accounts")
        .select("id, currency, exchange_rate_type")
        .eq("is_active", true);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  // FX helpers (same logic as useCashFlow)
  const getRateForDate = useMemo(() => {
    const rates = exchangeRatesQuery.data || [];
    return (date: string, rateType: string, pair: string): number => {
      const found = rates.find(
        (r) => r.rate_type === rateType && r.currency_pair === pair && r.effective_date <= date
      );
      // Explicit miss: 0 means "no rate found" so callers never silently treat
      // an unconverted amount as if it were already in the display currency.
      return found ? Number(found.rate) : 0;
    };
  }, [exchangeRatesQuery.data]);

  const convertAmount = useMemo(() => {
    const PAIR_MAP: Record<string, string> = {
      USD: "USD/ETB",
      EUR: "EUR/ETB",
      GBP: "GBP/ETB",
    };
    return (
      amount: number,
      src: string,
      date: string,
      rateType = "bank_rate",
      requireRate = false
    ): number => {
      if (src === displayCurrency) return amount;
      const direct = getRateForDate(date, rateType, `${src}/${displayCurrency}`);
      if (direct > 0 && direct !== 1) return amount * direct;
      const inv = getRateForDate(date, rateType, `${displayCurrency}/${src}`);
      if (inv > 0 && inv !== 1) return amount / inv;
      if (displayCurrency === "ETB" && PAIR_MAP[src]) {
        const r = getRateForDate(date, rateType, PAIR_MAP[src]);
        if (r > 0) return amount * r;
        if (!requireRate) return amount;
      }
      if (src === "ETB" && PAIR_MAP[displayCurrency]) {
        const r = getRateForDate(date, rateType, PAIR_MAP[displayCurrency]);
        if (r > 0) return amount / r;
        if (!requireRate) return amount;
      }
      if (PAIR_MAP[src] && PAIR_MAP[displayCurrency]) {
        const a = getRateForDate(date, rateType, PAIR_MAP[src]);
        const b = getRateForDate(date, rateType, PAIR_MAP[displayCurrency]);
        if (a > 0 && b > 0) return (amount * a) / b;
      }
      if (requireRate) {
        console.error(
          `[P&L] Missing required ${rateType} FX rate for ${src}->${displayCurrency} as of ${date}`
        );
        return 0;
      }
      return amount;
    };
  }, [displayCurrency, getRateForDate]);

  // Period math
  const periodKeys = useMemo(() => {
    if (!dateFrom || !dateTo) return [];
    const f = new Date(dateFrom);
    const t = new Date(dateTo);
    if (viewPeriod === "daily")
      return eachDayOfInterval({ start: f, end: t }).map((d) => format(d, "yyyy-MM-dd"));
    if (viewPeriod === "weekly")
      return eachWeekOfInterval({ start: f, end: t }, { weekStartsOn: 1 })
        .map((d) => format(d, "yyyy-MM-dd"));
    if (viewPeriod === "quarterly")
      return eachQuarterOfInterval({ start: f, end: t })
        .map((d) => `${format(d, "yyyy")}-Q${getQuarter(d)}`);
    return eachMonthOfInterval({ start: f, end: t }).map((d) => format(d, "yyyy-MM"));
  }, [dateFrom, dateTo, viewPeriod]);

  const dateToPeriodKey = useMemo(() => {
    return (dateStr: string): string => {
      const d = new Date(dateStr);
      if (viewPeriod === "daily") return format(d, "yyyy-MM-dd");
      if (viewPeriod === "weekly") return format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd");
      if (viewPeriod === "quarterly") return `${format(d, "yyyy")}-Q${getQuarter(d)}`;
      return format(d, "yyyy-MM");
    };
  }, [viewPeriod]);

  const formatPeriodKey = useMemo(() => {
    return (k: string): string => {
      if (viewPeriod === "daily") return format(new Date(k), "MMM d");
      if (viewPeriod === "weekly") return `W ${format(new Date(k), "MMM d")}`;
      if (viewPeriod === "quarterly") return k.replace("-", " ");
      return format(new Date(`${k}-01`), "MMM yyyy");
    };
  }, [viewPeriod]);

  const { rows, periodLabels, purchaseValues } = useMemo(() => {
    const expenses = expensesQuery.data || [];
    const categories = categoriesQuery.data || [];
    const sales = salesQuery.data || [];
    const fees = feesQuery.data || [];
    const b2b = b2bQuery.data || [];
    const bankAccounts = bankAccountsQuery.data || [];

    const categoryMap = new Map(categories.map((c: any) => [c.id, c]));
    const bankMap = new Map(bankAccounts.map((a: any) => [a.id, a]));

    const getGroupLabel = (categoryId: string | null): string => {
      if (!categoryId) return "Other Expenses";
      let current = categoryMap.get(categoryId);
      while (current) {
        if (current.category_type === "cost_group") return current.name;
        if (current.parent_id) {
          const parent = categoryMap.get(current.parent_id);
          if (parent && parent.category_type === "cost_group") return parent.name;
          current = parent || null;
        } else break;
      }
      return categoryMap.get(categoryId)?.name || "Other Expenses";
    };
    const getPositionLabel = (categoryId: string | null): string =>
      (categoryId && categoryMap.get(categoryId)?.name) || "Uncategorized";

    const createRow = (): Map<string, number> => new Map(periodKeys.map((k) => [k, 0]));

    // Revenue rows
    const chipchipSalesRow = createRow();
    const chipchipFeesRow = createRow();
    const b2bSalesRow = createRow();
    const dedicatedPurchasesRow = createRow();

    for (const s of sales as any[]) {
      const pk = dateToPeriodKey(s.date);
      if (!periodKeys.includes(pk)) continue;
      const conv = convertAmount(Number(s.total_gmv) || 0, "ETB", s.date, "bank_rate");
      chipchipSalesRow.set(pk, (chipchipSalesRow.get(pk) || 0) + conv);
    }
    for (const f of fees as any[]) {
      const pk = dateToPeriodKey(f.date);
      if (!periodKeys.includes(pk)) continue;
      const conv = convertAmount(Number(f.combined_total) || 0, "ETB", f.date, "bank_rate");
      chipchipFeesRow.set(pk, (chipchipFeesRow.get(pk) || 0) + conv);
    }
    for (const s of b2b as any[]) {
      const pk = dateToPeriodKey(s.sale_date);
      if (!periodKeys.includes(pk)) continue;
      const conv = convertAmount(Number(s.total_revenue) || 0, "ETB", s.sale_date, "bank_rate");
      b2bSalesRow.set(pk, (b2bSalesRow.get(pk) || 0) + conv);
    }

    // Expense grouping with rent amortization
    type PositionEntry = { label: string; values: Map<string, number> };
    const expenseGroups = new Map<string, Map<string, number>>();
    const positionGroups = new Map<string, Map<string, PositionEntry>>();

    const bumpGroup = (label: string, pk: string, amt: number) => {
      if (!expenseGroups.has(label)) expenseGroups.set(label, createRow());
      const r = expenseGroups.get(label)!;
      r.set(pk, (r.get(pk) || 0) + amt);
    };
    const bumpPos = (
      label: string,
      catId: string | null,
      posLabel: string,
      pk: string,
      amt: number
    ) => {
      if (!positionGroups.has(label)) positionGroups.set(label, new Map());
      const posMap = positionGroups.get(label)!;
      const key = catId || "__uncategorized__";
      if (!posMap.has(key)) posMap.set(key, { label: posLabel, values: createRow() });
      const pe = posMap.get(key)!;
      pe.values.set(pk, (pe.values.get(pk) || 0) + amt);
    };

    for (const exp of expenses as any[]) {
      const rawAmount = exp.net_payment ?? exp.total_amount ?? exp.amount ?? 0;
      const currency = exp.currency || "ETB";
      const bank = exp.bank_account_id ? bankMap.get(exp.bank_account_id) : null;

      const groupLabel = getGroupLabel(exp.category_id);
      const positionLabel = getPositionLabel(exp.category_id);
      // Purchases are perishable goods bought on company accounts: always the
      // bank USD/ETB rate, never amortized, never depreciated.
      const isPurchase = isPurchaseLine(groupLabel, positionLabel);

      if (isPurchase) {
        const purchaseAmount = convertPurchaseAtBankRate(
          Number(rawAmount) || 0,
          currency,
          displayCurrency,
          exp.expense_date,
          exchangeRatesQuery.data || []
        );
        if (purchaseAmount === null) {
          console.error(
            `[P&L] Missing required unscoped bank FX rate for purchase ${exp.id} (${currency}->${displayCurrency}) as of ${exp.expense_date}`
          );
          continue;
        }
        const purchasePeriod = dateToPeriodKey(exp.expense_date);
        if (periodKeys.includes(purchasePeriod)) {
          dedicatedPurchasesRow.set(
            purchasePeriod,
            (dedicatedPurchasesRow.get(purchasePeriod) || 0) + purchaseAmount
          );
        }
      }

      const rateType = isPurchase
        ? "bank_rate"
        : (bank as any)?.exchange_rate_type || "bank_rate";
      const converted = convertAmount(rawAmount, currency, exp.expense_date, rateType, isPurchase);

      // Fixed assets: capitalize and depreciate straight-line over 60 months.
      // Only paid assets depreciate (matches Balance Sheet treatment).
      if (exp.category_id === ASSET_CATEGORY_ID && !isPurchase) {
        if (exp.payment_status !== "paid") continue;
        const windowStart = new Date(exp.expense_date);
        const windowEnd = addDays(addMonths(windowStart, DEPRECIATION_MONTHS), -1);
        const totalDays = differenceInCalendarDays(windowEnd, windowStart) + 1;
        if (totalDays <= 0) continue;
        const perDay = converted / totalDays;

        let cursor = startOfMonth(windowStart);
        while (cursor <= windowEnd) {
          const monthStart = cursor;
          const monthEnd = endOfMonth(cursor);
          const sliceStart = dfMax([monthStart, windowStart]);
          const sliceEnd = dfMin([monthEnd, windowEnd]);
          const daysInSlice = differenceInCalendarDays(sliceEnd, sliceStart) + 1;
          if (daysInSlice > 0) {
            const amt = perDay * daysInSlice;
            const pk = dateToPeriodKey(format(sliceStart, "yyyy-MM-dd"));
            if (periodKeys.includes(pk)) {
              bumpGroup("Depreciation", pk, amt);
              bumpPos("Depreciation", "__depreciation__", "Depreciation (Fixed Assets)", pk, amt);
            }
          }
          cursor = addMonths(cursor, 1);
        }
        continue;
      }

      const label = groupLabel;
      const posLabel = positionLabel;
      const months = isPurchase ? 1 : Math.max(1, Number(exp.coverage_months) || 1);

      const windowStart = new Date(exp.expense_date);
      const windowEnd = addDays(addMonths(windowStart, months), -1);
      const totalDays = differenceInCalendarDays(windowEnd, windowStart) + 1;
      if (totalDays <= 0) continue;
      const perDay = converted / totalDays;

      // Walk each calendar month the window touches; add perDay * days-in-month.
      let cursor = startOfMonth(windowStart);
      while (cursor <= windowEnd) {
        const monthStart = cursor;
        const monthEnd = endOfMonth(cursor);
        const sliceStart = dfMax([monthStart, windowStart]);
        const sliceEnd = dfMin([monthEnd, windowEnd]);
        const daysInSlice = differenceInCalendarDays(sliceEnd, sliceStart) + 1;
        if (daysInSlice > 0) {
          const amt = perDay * daysInSlice;
          const pk = dateToPeriodKey(format(sliceStart, "yyyy-MM-dd"));
          if (periodKeys.includes(pk)) {
            bumpGroup(label, pk, amt);
            bumpPos(label, exp.category_id, posLabel, pk, amt);
          }
        }
        cursor = addMonths(cursor, 1);
      }
    }

    // Build output rows: same schema as useCashFlowStatementDetails so
    // FinancialReport-style buildRows can be reused.
    const totalSalesRow = createRow();
    for (const pk of periodKeys) {
      totalSalesRow.set(
        pk,
        (chipchipSalesRow.get(pk) || 0) +
          (chipchipFeesRow.get(pk) || 0) +
          (b2bSalesRow.get(pk) || 0)
      );
    }

    const allRows: CashFlowRow[] = [];
    allRows.push({
      key: "op-header",
      label: "OPERATING ACTIVITIES",
      isHeader: true,
      values: createRow(),
    });
    allRows.push({ key: "chipchip-sales", label: "ChipChip Sales", indent: 1, values: chipchipSalesRow });
    allRows.push({ key: "chipchip-fees", label: "ChipChip (Delivery & Packaging Fee)", indent: 1, values: chipchipFeesRow });
    allRows.push({ key: "b2b-sales", label: "B2B Sales", indent: 1, values: b2bSalesRow });
    allRows.push({ key: "total-sales", label: "Total Sales Inflow", isTotal: true, indent: 1, values: totalSalesRow });
    allRows.push({
      key: "pnl-purchases-dedicated",
      label: "Purchases",
      indent: 1,
      values: dedicatedPurchasesRow,
    });

    const buildChildren = (groupLabel: string, keyPrefix: string): CashFlowRow[] => {
      const positions = positionGroups.get(groupLabel);
      if (!positions || positions.size <= 1) return [];
      return [...positions.entries()]
        .sort((a, b) => a[1].label.localeCompare(b[1].label))
        .map(([catId, e]) => ({
          key: `${keyPrefix}-pos-${catId}`,
          label: e.label,
          indent: 2,
          values: e.values,
          categoryId: catId === "__uncategorized__" ? undefined : catId,
        }));
    };

    const sorted = [...expenseGroups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    for (const [label, row] of sorted) {
      allRows.push({
        key: `op-exp-${label}`,
        label: `(${label})`,
        indent: 1,
        values: row,
        children: buildChildren(label, `op-exp-${label}`),
      });
    }

    const labels = periodKeys.map((k) => ({ key: k, label: formatPeriodKey(k) }));

    return { rows: allRows, periodLabels: labels, purchaseValues: dedicatedPurchasesRow };
  }, [
    expensesQuery.data,
    categoriesQuery.data,
    salesQuery.data,
    feesQuery.data,
    b2bQuery.data,
    bankAccountsQuery.data,
    exchangeRatesQuery.data,
    periodKeys,
    dateToPeriodKey,
    convertAmount,
    formatPeriodKey,
    displayCurrency,
  ]);

  const isLoading =
    exchangeRatesQuery.isFetching ||
    expensesQuery.isFetching ||
    categoriesQuery.isLoading ||
    salesQuery.isLoading ||
    feesQuery.isLoading ||
    b2bQuery.isLoading ||
    bankAccountsQuery.isLoading;

  return { rows, periodLabels, periodKeys, purchaseValues, isLoading, displayCurrency };
}
